/**
 * @file     web-format-date.js     misc utilities for nicely formatting dates, times, etc.
 * @author   Ryan Rossiter, ryan@kingsds.network
 * @date     Sep 2020
 */

/** Format a Date to Common Log Format standard
 *  @param      date            Optional Date to format; default: now()
 *  @return {string} in the format "[06/Aug/1982:13:06:00 -0500]"
 */
exports.webFormatDate = function (d = new Date()) {
  const date = [
    d.toLocaleString('en-CA', { day: '2-digit' }),
    d.toLocaleString('en-CA', { month: 'short' }),
    d.toLocaleString('en-CA', { year: 'numeric' }),
  ].join('/');
  const time = d.toLocaleString('en-CA', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' });
  const tz = (d.getTimezoneOffset() > 0 ? '-' : '+')
    + Math.abs(d.getTimezoneOffset() / .60).toLocaleString('en-CA', { minimumIntegerDigits: 4, useGrouping: false });

  return '[' + date + ':' + time + ' ' + tz + ']';
}

/** Format a time interval expressed in milliseconds into idiomatic English, e.g.
 *  "3 years, 11 days, 18 hours, 53:24.1"
 *
 *  @param {number}   ms        The number of milliseconds in the time interval
 *  @param {object}   options   An optional options object with any of the following properties:
 *                              - prefix:   A prefix to put in front of each temporal unit; use eg. to express
 *                                          intervals in core-years instead of years.
 *                              - locale:   The locale for which to format the string (NYI)
 *                              - collapse: remove leading quantities whose value is one, so that we can
 *                                          generate "for the next day" instead of "for the next 1 day"
 *                              - short:    remove trailing quantities whose value is zero, so that we
 *                                          can generate "for 1 day" instead of "for 1 day 00:00:00.0"
 *  @param
 *
 *  @returns the human-formatted time interval as a string
 */
exports.humanFriendlyTimeInterval = function humanFriendlyTimeInterval(ms, options)
{
  if (!options)
    options = {};
  if (!options.prefix)
    options.prefix = '';

  if (options.locale)
    console.warn(`Warning: locale ${options.locale} not supported; using default`);

  ms = Math.floor(ms);

  function sinpl(amount, singular, plural, overrideOptions)
  {
    const myOptions = overrideOptions ? Object.assign({}, options, overrideOptions) : options;
    if (amount === 1)
      return myOptions.collapse ? myOptions.prefix + singular : amount + ' ' + myOptions.prefix + singular;
    return amount + ' ' + myOptions.prefix + plural;
  }

  function doUnit(sz, nameSin, namePlu)
  {
    const remainMs = ms % sz;
    const whole = Math.floor(ms / sz).toFixed(0);
    const base = sinpl(whole, nameSin, namePlu);

    if (remainMs === 0 && options.short || options.collapse)
      return base;
    else
      return base + ', ' + humanFriendlyTimeInterval(remainMs, options);
  }

  if (ms >= 365 * 86400000)
    return doUnit(365 * 86400000, 'year', 'years');
  if (ms >=   7 * 86400000)
    return doUnit(  7 * 86400000, 'week', 'weekss');
  if (ms >=   1 * 86400000)
    return doUnit(  1 * 86400000, 'day', 'days');

  /* below two days => so should show hours, mm:ss.0 */

  const h  = Math.floor(ms / 3600000) % 24;
  const m  = Math.floor(ms /   60000) % 60;
  const s  = Math.floor(ms /    1000) % 60;
  const ds = Math.floor(Math.round(ms % 1000) / 100);

  out = '';
  if (s || ds)
    out = sinpl(s + ds/10, 'second', 'seconds');
  if (m)
    out = sinpl(m, 'minute', 'minutes', { collapse: options.collapse && !(h || s || ds) }) + ' ' + out;
  if (h)
    out = sinpl(h, 'hour', 'hours', { collapse: options.collapse && !(m || s || ds) }) + ' ' + out;

  if (!out)
    out = 'instant';

  return out;
}
